/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */

import { Game } from './Ballistic/Game';
import { BoxHelper } from './Ballistic/BoxHelper';
import { CapNames, Director, Stage, RenderConfig, DefaultCaps, DefaultNavigators, StageDesc, Events } from '@sick-davinci/glow';
import { addStatsWidget } from '@sick-davinci/glow-utils';

import { Checkbox, DropDown, Slider, Button } from '@sick-davinci/basic-elements';

export const dependencies = [Checkbox, DropDown, Button, Slider];  // avoid wepack removal

/**
 * Demo:    Ballistic
 * Description:
 *   This demo application implements an interactive game shown at the GBC05/08 Technology Days 2018.
 *   2 Visionaries capture the environment and extract the position of a cuboid and send it's position to the game (RegistrationDemo).
 *   A ball is shot along a physical path and is deflected by the environment and the cuboid.
 *   If the ball hits the target (rotating shader animation), the player wins.
 *   The ball shot is triggered by the TriggerButtonApp app.
 * Features:
 *  - Orbit navigation
 *  - Skybox
 *  - Keyboard interaction
 *  - Game logic
 *  - Animations
 */
export const run = ((viewParent: HTMLElement) => {

  // ** setup the stage *
  const setup: StageDesc = {
    cameras: [
      {
        name: 'frontCamera',
        from: [50, 120, -5],
        to: [50, 0, 50],
        pivot: [50, 0, 50],
        up: [0, 1, 0],
        fov: 45,
      },
    ],
    scenes: [
      { name: 'default' },
    ],
    viewports: [
      {
        scene: 'default',
        camera: 'frontCamera',
        navigator: 'orbit',
        left: 0,
        top: 0,
        width: 1.0,
        height: 1.0,
        bgcolor: 0x888800,
      },
    ],
  };

  const stage = new Stage(viewParent, setup, new RenderConfig({ enableShadows: true, enableAntialiasing: true }));

  DefaultNavigators.initOrbit(stage, 'orbit', {
    straightAhead: {
      speed: 15,
    },
  });

  // ** add fps-meter to the scene
  addStatsWidget(stage);

  stage.start();  // start visualizing data

  const director = new Director(stage);
  DefaultCaps.init(director);

  // ** Load scenery
  director.handle(CapNames.OBJFILE, {
    objFile: 'davinci room-1.obj',
    mtlFile: 'davinci room-1.mtl',
    baseUrl: './resources/'
    , orientation: { scale: [120, 120, 120], position: [-888, -107.5, 775] },
  });

  director.handle(CapNames.SKYBOX, {
    textureList: [
      './resources/interstellar_lf.jpg',
      './resources/interstellar_rt.jpg',
      './resources/interstellar_up.jpg',
      './resources/interstellar_dn.jpg',
      './resources/interstellar_ft.jpg',
      './resources/interstellar_bk.jpg',
    ],
  });

  // ** Game logic **
  let levelNum = 0;
  const showRoute = document.getElementById('showroute') as Checkbox;
  showRoute.addEventListener('change', ((evt) => game.enableRoute(showRoute.checked)));

  const levelSel = document.getElementById('level') as DropDown;
  levelNum = Number.parseInt(levelSel.value);
  levelSel.addEventListener('change', ((evt) => {
    stage.clearTaggedObjects('level');
    game.setLevel(Number.parseInt((evt as any).detail));
    game.drawRawLevel();
  }));
  const game = new Game(stage, 39, levelNum);

  let locked = false;
  let won = false;
  let angleDeg = 270;
  let centerX = 80;
  let centerY = 80;

  function updatePlank() {
    game.updatePlank(centerX, centerY, angleDeg * Math.PI / 180);
  }
  director.handle(CapNames.LIGHT, { lightType: 'point', castShadow: true, color: 0x444455, orientation: { position: [35, 40, -10] } });
  director.handle(CapNames.LIGHT, { lightType: 'point', castShadow: true, color: 0x444455, orientation: { position: [70, 40, 110] } });
  director.handle(CapNames.LIGHT, { lightType: 'ambient', color: 0xbbbbbb });

  game.drawRawLevel();

  {
    const plank = BoxHelper.shape2Plank(BoxHelper.box);
    centerX = plank.x;
    centerY = plank.y;
    angleDeg = plank.angle * 180 / Math.PI;
    updatePlank();
  }

  // ** Get coboid position and trigger from device
  const ws = new WebSocket('ws://127.0.0.1/crownJSON');
  ws.onopen = () => {

    const functionCall = {
      header: {
        type: 'FunctionCall',
        clientId: 0,
        function: 'RegistrationDemo/register',
      },
      data: {
        eventname: 'OnNewData',
      },
    };

    ws.send(JSON.stringify(functionCall));

    functionCall.header.function = 'TriggerButtonApp/register';
    functionCall.data.eventname = 'OnButtonPressed';

    ws.send(JSON.stringify(functionCall));

    ws.onmessage = (msg) => {
      if (msg.data === '') { return; }
      const obj = JSON.parse(msg.data);
      if (!!obj.data.shape) {
        const plank = BoxHelper.shape2Plank(obj.data.shape.data);
        centerX = plank.x;
        centerY = plank.y;
        angleDeg = plank.angle * 180 / Math.PI;
        updatePlank();
      } else if (!!obj.data.newParam0 && !locked) {
        locked = true;
        won = game.startAnimation();
      }
    };
  };
  function foundWinner() {
    console.log('You win ;-)');
    const functionCall = {
      header: {
        type: 'FunctionCall',
        clientId: 0,
        function: 'TriggerButtonApp/alarm',
      },
      data: {
        // eventname: 'OnNewData'
      },
    };
    ws.send(JSON.stringify(functionCall));
  }
  viewParent.addEventListener(Events.ACTORFINISHED, () => {
    locked = false;
    if (won) {
      foundWinner();
    }
  });

  const plankRot = document.getElementById('plankRotation') as Slider;
  plankRot.addEventListener('input', ((evt) => {
    angleDeg = (evt as any).detail as number;
    updatePlank();
  }));
  angleDeg = plankRot.value;
  updatePlank();

  const left = document.getElementById('left') as Button;
  left.addEventListener('click', (() => {
    centerX += 3;
    updatePlank();
  }), true);
  const right = document.getElementById('right') as Button;
  right.addEventListener('click', (() => {
    centerX -= 3;
    updatePlank();
  }), true);
  const up = document.getElementById('up') as Button;
  up.addEventListener('click', (() => {
    centerY += 3;
    updatePlank();
  }), true);
  const down = document.getElementById('down') as Button;
  down.addEventListener('click', (() => {
    centerY -= 3;
    updatePlank();
  }), true);
  const shoot = document.getElementById('shoot') as Button;
  shoot.addEventListener('click', (() => {
    locked = true;
    won = game.startAnimation();  // shoot the ball
  }), true);

  function registerKeyEvents(el: any) {
    el.addEventListener('keydown', (event: any) => {
      event.preventDefault();
      if (event.which === ' '.charCodeAt(0) && !locked) {
        locked = true;
        won = game.startAnimation();  // shoot the ball
      }

      if (event.which === 'Q'.charCodeAt(0)) {
        angleDeg = (angleDeg + 3) % 180;
        updatePlank();
      }

      if (event.which === 'E'.charCodeAt(0)) {
        angleDeg = (angleDeg - 3) % 180;
        updatePlank();
      }

      if (event.which === 'W'.charCodeAt(0)) {
        centerY += 3;
        updatePlank();
      }
      if (event.which === 'S'.charCodeAt(0)) {
        centerY -= 3;
        updatePlank();
      }
      if (event.which === 'A'.charCodeAt(0)) {
        centerX += 3;
        updatePlank();
      }
      if (event.which === 'D'.charCodeAt(0)) {
        centerX -= 3;
        updatePlank();
      }
    });
  }

  // if embedded in iframe register iframe
  if (window.parent) {
    registerKeyEvents(window.parent);
  }
  // register window itself
  registerKeyEvents(window);
});
